import React, { useRef } from 'react';
import { TextField, Box, Typography, Tooltip, Switch } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { styled } from '@mui/material/styles';

import {
  Step2CardsContainer,
  Step2Card,
  InfoCardTitle,
  FieldGroup,
  FieldLabel,
  FieldHint,
  ImageUploadArea,
  ImageUploadIcon,
  ImageUploadText,
  SmallUploadButton,
  ImagePreviewWrapper,
  ImagePreview,
  ImageRemoveButton,
  AIToggleBoxNew,
  AIToggleContent,
  AIIcon,
  AIToggleLabelNew,
  StyledSwitch,
  RichTextContainer,
  BottomActionsNew,
  CancelButtonNew,
  PrevButtonNew,
  SaveButtonNew,
  ImageUploadBox,
} from './PassEditStyled';

// 추가 스타일 컴포넌트
const ImageGridContainer = styled(Box)({
  display: 'flex',
  gap: '8px',
  flexWrap: 'wrap',
  marginTop: '16px',
});

const SmallImagePreview = styled(Box)({
  position: 'relative',
  width: '80px',
  height: '80px',
  borderRadius: '4px',
  overflow: 'hidden',
});

const SmallImageRemoveButton = styled(Box)({
  position: 'absolute',
  top: '4px',
  right: '4px',
  width: '16px',
  height: '16px',
  borderRadius: '50%',
  background: 'rgba(0,0,0,0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  color: '#fff',
});

const ImageCountText = styled(Typography)({
  fontSize: '13px',
  fontWeight: 500,
  color: '#737373',
  marginTop: '8px',
});

const ImageInfoText = styled(Typography)({
  fontSize: '12px',
  color: '#A6A6A6',
  marginTop: '8px',
  lineHeight: 1.5,
});

const CurriculumCard = styled(Box)({
  background: '#F3F9FA',
  borderRadius: '8px',
  padding: '16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
});

const CurriculumHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

const CurriculumDeleteIcon = styled(Box)({
  color: '#F87171',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
});

const AddCurriculumButton = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',
  cursor: 'pointer',
  color: '#404040',
  width: '100%',
  '&:hover': {
    opacity: 0.7,
  },
});

function PassEditStep02({
  control,
  setValue,
  passData,
  setPassData,
  curriculumList,
  setCurriculumList,
  aiToggle,
  setAiToggle,
  uploadImages,
  deleteVoucher,
  handleCurriculumChange,
  handleCurriculumImageUpload,
  handleCurriculumImageDelete,
  addCurriculum,
  deleteCurriculum,
  onPrev,
  onSubmit,
  onCancel,
  loader,
  isEditMode,
  canSave = true,
  exposureYn,
  setExposureYn,
}) {
  const passImageInputRef = useRef(null);

  return (
    <>
      <Step2CardsContainer>
        {/* Card 1: 이용권 이미지 */}
        <Step2Card width="360px">
          <InfoCardTitle>이용권 이미지</InfoCardTitle>

          {/* 이미지 업로드 영역 */}
          {(!passData?.voucherList || passData.voucherList.length === 0) ? (
            <ImageUploadArea onClick={() => passImageInputRef.current?.click()}>
              <ImageUploadIcon>
                <CloudUploadOutlinedIcon sx={{ fontSize: 28, color: '#7DC8CF' }} />
              </ImageUploadIcon>
              <ImageUploadText>
                클릭하여 업로드 또는
                <br />
                드래그 앤 드롭
              </ImageUploadText>
            </ImageUploadArea>
          ) : (
            <Box>
              {/* 메인 이미지 프리뷰 */}
              <ImagePreviewWrapper sx={{ width: '100%', height: '200px' }}>
                <ImagePreview
                  src={
                    passData.voucherList[0]?.voucherUrl ??
                    (window.URL || window.webkitURL).createObjectURL(passData.voucherList[0]?.voucherImg)
                  }
                  alt="main-pass"
                />
                <ImageRemoveButton onClick={() => deleteVoucher(0)}>
                  <CloseIcon sx={{ fontSize: 14 }} />
                </ImageRemoveButton>
              </ImagePreviewWrapper>
            </Box>
          )}

          {/* 추가 이미지 그리드 */}
          <ImageGridContainer>
            {passData?.voucherList?.slice(1).map((img, index) => (
              <SmallImagePreview key={index + 1}>
                <img
                  src={img.voucherUrl ?? (window.URL || window.webkitURL).createObjectURL(img.voucherImg)}
                  alt={`pass-${index + 1}`}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
                <SmallImageRemoveButton onClick={() => deleteVoucher(index + 1)}>
                  <CloseIcon sx={{ fontSize: 10 }} />
                </SmallImageRemoveButton>
              </SmallImagePreview>
            ))}

            {/* 추가 업로드 버튼 */}
            {passData?.voucherList?.length > 0 && passData.voucherList.length < 7 && (
              <SmallUploadButton onClick={() => passImageInputRef.current?.click()}>
                <AddCircleOutlineIcon sx={{ fontSize: 24, color: '#A6A6A6' }} />
              </SmallUploadButton>
            )}
          </ImageGridContainer>

          <input
            type="file"
            accept="image/*"
            multiple
            ref={passImageInputRef}
            style={{ display: 'none' }}
            onChange={uploadImages}
          />

          <ImageCountText>{passData?.voucherList?.length || 0}/7</ImageCountText>
          <ImageInfoText>
            이미지는 최대 7장, PNG/JPEG파일. 파일크기 최대 8MB, 가로세로 비율은 4:3, 1200*900px 사이즈를 권장합니다.
          </ImageInfoText>
        </Step2Card>

        {/* Card 2: 커리큘럼 등록 - 주석처리 (필수 항목 아님) */}
        {/* <Step2Card width="420px">
          <InfoCardTitle>커리큘럼 등록</InfoCardTitle>

          {curriculumList.map((curriculum, curIndex) => (
            <CurriculumCard key={curIndex}>
              {curriculumList.length > 1 && (
                <CurriculumHeader>
                  <FieldLabel>커리큘럼 {curIndex + 1}</FieldLabel>
                  <CurriculumDeleteIcon onClick={() => deleteCurriculum(curIndex)}>
                    <CloseIcon sx={{ fontSize: 18 }} />
                  </CurriculumDeleteIcon>
                </CurriculumHeader>
              )}

              <FieldGroup>
                <FieldLabel>제목</FieldLabel>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="제목을 입력해 주세요."
                  value={curriculum.title || ''}
                  onChange={e => handleCurriculumChange(curIndex, 'title', e.target.value)}
                  sx={{
                    background: '#fff',
                    borderRadius: '4px',
                    '& .MuiOutlinedInput-root': {
                      height: '44px',
                    },
                  }}
                />
              </FieldGroup>

              <FieldGroup>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FieldLabel>사진 업로드</FieldLabel>
                  <FieldHint>*최대 7장</FieldHint>
                </Box>
                <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <ImageUploadBox as="label" sx={{ width: '80px', height: '80px' }}>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      style={{ display: 'none' }}
                      onChange={e => handleCurriculumImageUpload(curIndex, e)}
                    />
                    <AddCircleOutlineIcon sx={{ fontSize: 24, color: '#A6A6A6' }} />
                  </ImageUploadBox>
                  {(curriculum.images || []).map((img, imgIndex) => (
                    <Box key={imgIndex} sx={{ position: 'relative', width: '80px', height: '80px' }}>
                      <img
                        src={img.url ?? (window.URL || window.webkitURL).createObjectURL(img.file)}
                        alt={`cur-${curIndex}-${imgIndex}`}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px' }}
                      />
                      <SmallImageRemoveButton onClick={() => handleCurriculumImageDelete(curIndex, imgIndex)}>
                        <CloseIcon sx={{ fontSize: 10 }} />
                      </SmallImageRemoveButton>
                    </Box>
                  ))}
                </Box>
              </FieldGroup>

              <FieldGroup>
                <FieldLabel>내용</FieldLabel>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  maxRows={3}
                  placeholder="내용을 입력해 주세요."
                  value={curriculum.description || ''}
                  onChange={e => handleCurriculumChange(curIndex, 'description', e.target.value)}
                  sx={{
                    background: '#fff',
                    borderRadius: '4px',
                    '& .MuiOutlinedInput-root': {
                      minHeight: '80px',
                      alignItems: 'flex-start',
                    },
                  }}
                />
              </FieldGroup>
            </CurriculumCard>
          ))}

          <AddCurriculumButton onClick={addCurriculum}>
            <AddCircleOutlineIcon sx={{ fontSize: 24 }} />
            <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>커리큘럼 추가</Typography>
          </AddCurriculumButton>
        </Step2Card> */}

        {/* Card 3: 이용권 상세 */}
        <Step2Card sx={{ flex: 1 }}>
          <InfoCardTitle>이용권 상세</InfoCardTitle>

          {/* AI 토글 */}
          <AIToggleBoxNew>
            <AIToggleContent>
              <AIIcon>
                <AutoAwesomeIcon sx={{ fontSize: 20 }} />
              </AIIcon>
              <AIToggleLabelNew>AI로 작성하기</AIToggleLabelNew>
            </AIToggleContent>
            <StyledSwitch
              checked={aiToggle}
              onChange={e => setAiToggle(e.target.checked)}
            />
          </AIToggleBoxNew>

          {/* 내용 */}
          <FieldGroup sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <FieldLabel>내용</FieldLabel>
            <RichTextContainer sx={{ flex: 1 }}>
              <CKEditor
                editor={ClassicEditor}
                data={passData?.classDetail || ''}
                onChange={(event, editor) => {
                  const data = editor.getData();
                  setPassData(prev => ({ ...prev, classDetail: data }));
                  setValue('classDetail', data);
                }}
                config={{
                  placeholder: '이용권 상세 내용을 입력해 주세요',
                }}
              />
            </RichTextContainer>
          </FieldGroup>
        </Step2Card>
      </Step2CardsContainer>

      {/* Bottom Buttons */}
      <BottomActionsNew>
        <CancelButtonNew onClick={onCancel}>취소</CancelButtonNew>
        <PrevButtonNew onClick={onPrev}>이전</PrevButtonNew>
        <Tooltip title={!canSave ? '권한이 없습니다' : ''} arrow>
          <span>
            <SaveButtonNew onClick={onSubmit} disabled={loader || !canSave}>
              {isEditMode ? '저장' : '등록'}
            </SaveButtonNew>
          </span>
        </Tooltip>
      </BottomActionsNew>
    </>
  );
}

export default PassEditStep02;
